import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowDown, FaArrowRight, FaArrowUp, FaBell, FaBoxOpen, FaChartBar,
  FaChartLine, FaChartPie, FaCheck, FaCheckCircle, FaChevronLeft,
  FaChevronRight, FaClock, FaCog, FaDatabase, FaDollarSign, FaEdit, FaEye,
  FaExclamationTriangle, FaFlag, FaFire, FaGripVertical, FaInfoCircle,
  FaLayerGroup, FaList, FaPercentage, FaPlus, FaRedo, FaSave, FaSearch,
  FaShieldAlt, FaSignInAlt, FaSignOutAlt, FaStore, FaTable, FaTachometerAlt,
  FaTimes, FaTrash, FaTruck, FaUserCheck, FaUsers, FaUtensils,
} from "react-icons/fa";

/* -------------------------------------------------------------------------- */
/*  Config                                                                    */
/* -------------------------------------------------------------------------- */

const CITRIOT_LOGO = "/citriot_logo.webp";
const EMPIRE_LOGO = "/empirelogo.webp";

const STORAGE_KEY = "empireDashboardBuilder";
const SESSION_MODULE_KEY = "empireSelectedModules";
const LOGIN_PATH = "/login";

const MODULES = [
  { id: "dashboard", title: "Dashboard", icon: FaTachometerAlt, description: "Overall operational overview" },
  { id: "writer", title: "Writer", icon: FaEdit, description: "Writer activity and visibility" },
  { id: "alerts", title: "Alerts", icon: FaBell, description: "Operational alerts and events" },
  { id: "hygiene-safety", title: "Hygiene & Safety", icon: FaShieldAlt, description: "Safety and hygiene compliance" },
  { id: "captain-behaviour", title: "Captain Behaviour", icon: FaUserCheck, description: "Captain activity and behaviour" },
  { id: "wastage-monitoring", title: "Wastage Monitoring", icon: FaTrash, description: "Wastage and bin monitoring" },
  { id: "cash-counter", title: "Cash Counter", icon: FaDollarSign, description: "Cash counter activity" },
  { id: "material-inward", title: "Material Inward", icon: FaTruck, description: "Material receiving activity" },
  { id: "store-entry-exit", title: "Store Entry & Exit", icon: FaSignInAlt, description: "Store movement tracking" },
  { id: "dining-area-footfall", title: "Dining Area Footfall", icon: FaUsers, description: "Customer footfall analytics" },
  { id: "kabab-monitoring", title: "Kabab Monitoring", icon: FaFire, description: "Kabab preparation monitoring" },
];

/* -------------------------------------------------------------------------- */
/*  Component data (small factories keep the definitions compact)             */
/* -------------------------------------------------------------------------- */

const kpi = (id, title, description, icon, color, value, trend, trendUp, secondary) => ({
  id, title, description, type: "kpi", icon, color, value, trend, trendUp, secondary,
});
const progress = (id, title, description, icon, color, value, pct, secondary) => ({
  id, title, description, type: "progress", icon, color, value, progress: pct, secondary,
});
const chart = (type) => (id, title, description, icon, color, labels, values) => ({
  id, title, description, type, icon, color, labels, values,
});
const line = chart("line");
const bar = chart("bar");
const area = chart("area");
const donut = chart("donut");
const compliance = (id, title, description, icon, color, items) => ({
  id, title, description, type: "compliance", icon, color, items,
});
const table = (id, title, description, icon, color, columns, rows) => ({
  id, title, description, type: "table", icon, color, columns, rows,
});
const activity = (id, title, description, icon, color, items) => ({
  id, title, description, type: "activity", icon, color,
  items: items.map(([t, time, status]) => ({ title: t, time, status })),
});
const summary = (id, title, description, icon, color, items) => ({
  id, title, description, type: "summary", icon, color, items,
});

const COMPONENTS = {
  dashboard: [
    kpi("dashboard-total-events", "Total Monitored Events", "Events monitored today", FaDatabase, "blue", "12,840", "+8.4%", true, "1,248 events this hour"),
    kpi("dashboard-compliance", "Overall Compliance", "Combined operational compliance", FaPercentage, "green", "94%", "+3.2%", true, "Target 90%"),
    kpi("dashboard-footfall", "Total Footfall", "Customers detected today", FaUsers, "purple", "8,426", "+11.6%", true, "Peak at 8 PM"),
    progress("dashboard-occupancy", "Dining Occupancy", "Current dining area occupancy", FaUtensils, "orange", "72%", 72, "144 of 200 seats occupied"),
    kpi("dashboard-alerts", "Alerts Today", "Operational alerts generated", FaBell, "red", "18", "-12.5%", false, "3 require attention"),
    kpi("dashboard-staff", "Key-role Staff", "Staff presence detected", FaUserCheck, "teal", "11 / 12", "+1", true, "91.7% availability"),
    line("dashboard-store-movement", "Store Entries & Exits", "Hourly movement activity", FaChartLine, "blue", ["10 AM", "11 AM", "12 PM", "1 PM", "2 PM", "3 PM", "4 PM"], [22, 34, 41, 58, 46, 63, 52]),
    bar("dashboard-footfall-hour", "Footfall by Hour", "Customer count by time", FaChartBar, "purple", ["12", "1", "2", "3", "4", "5", "6", "7", "8"], [280, 340, 410, 380, 520, 690, 840, 980, 1210]),
    donut("dashboard-cash-status", "Cash Counter Status", "Counter visibility status", FaDollarSign, "green", ["Visible", "Not Visible"], [96, 4]),
    line("dashboard-staff-attendance", "Staff Attendance", "Staff availability trend", FaUsers, "teal", ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"], [91, 94, 92, 96, 95, 93]),
    compliance("dashboard-compliance-overview", "Compliance Overview", "Operational compliance areas", FaShieldAlt, "green", [
      { label: "Hygiene", value: 96 }, { label: "Safety", value: 93 }, { label: "Staff", value: 91 }, { label: "Store", value: 95 },
    ]),
    table("dashboard-store-performance", "Store Performance", "Current store activity", FaStore, "blue", ["Store", "Events", "Compliance", "Status"], [
      ["Main Store", "4,280", "96%", "Healthy"], ["Kitchen Store", "3,740", "92%", "Healthy"],
      ["Dining Store", "2,960", "95%", "Healthy"], ["Receiving", "1,860", "89%", "Watch"],
    ]),
    activity("dashboard-recent-alerts", "Recent Alerts", "Latest operational events", FaBell, "red", [
      ["Dining occupancy crossed 85%", "4 min ago", "Warning"], ["Cash counter visibility restored", "11 min ago", "Resolved"],
      ["Material inward activity detected", "18 min ago", "Info"], ["Captain mobile usage detected", "24 min ago", "Info"],
    ]),
    summary("dashboard-material-activity", "Material Activity", "Recent material movement", FaBoxOpen, "orange", [
      ["Today's arrivals", "32"], ["Processed", "28"], ["Pending", "4"], ["Avg. processing", "14 min"],
    ]),
    area("dashboard-occupancy-trend", "Dining Occupancy Trend", "Occupancy throughout the day", FaUsers, "purple", ["12", "2", "4", "6", "8", "10"], [31, 42, 48, 61, 84, 72]),
  ],

  writer: [
    kpi("writer-visibility", "Writer Visibility", "Writer detection visibility", FaEye, "blue", "98%", "+4.2%", true, "Target 95%"),
    kpi("writer-events", "Writer Events", "Detected writing events", FaEdit, "purple", "1,284", "+9.8%", true, "Today"),
    line("writer-trend", "Writer Activity Trend", "Daily writing activity", FaChartLine, "blue", ["10", "11", "12", "1", "2", "3", "4"], [32, 45, 38, 64, 71, 62, 78]),
    progress("writer-compliance", "Writer Compliance", "Writing process compliance", FaCheckCircle, "green", "91%", 91, "9% requiring review"),
    activity("writer-activity", "Recent Writer Activity", "Latest detected events", FaList, "purple", [
      ["Writer activity detected", "2 min ago", "Active"], ["Form completion detected", "9 min ago", "Complete"],
      ["Writing area checked", "16 min ago", "Verified"],
    ]),
  ],

  alerts: [
    kpi("alerts-total", "Total Alerts", "Alerts generated today", FaBell, "red", "18", "-12.5%", false, "Compared with yesterday"),
    kpi("alerts-critical", "Critical Alerts", "Alerts requiring immediate action", FaExclamationTriangle, "red", "3", "-25%", false, "2 already acknowledged"),
    progress("alerts-resolution", "Resolution Rate", "Alerts resolved today", FaCheckCircle, "green", "89%", 89, "16 of 18 resolved"),
    bar("alerts-trend", "Alert Trend", "Alerts by hour", FaChartBar, "red", ["10", "11", "12", "1", "2", "3", "4"], [3, 5, 2, 4, 1, 6, 3]),
    activity("alerts-list", "Latest Alerts", "Most recent alerts", FaFlag, "red", [
      ["Dining occupancy high", "4 min ago", "Warning"], ["Cash counter visibility", "11 min ago", "Resolved"],
      ["Store entry detected", "18 min ago", "Info"], ["Kabab duration exceeded", "27 min ago", "Warning"],
    ]),
  ],

  "hygiene-safety": [
    kpi("hygiene-score", "Hygiene Compliance", "Overall hygiene score", FaShieldAlt, "green", "96%", "+2.8%", true, "Target 90%"),
    kpi("safety-score", "Safety Compliance", "Safety monitoring score", FaCheckCircle, "blue", "93%", "+4.1%", true, "Target 90%"),
    line("hygiene-trend", "Compliance Trend", "Weekly compliance trend", FaChartLine, "green", ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"], [88, 91, 93, 92, 95, 94, 96]),
    compliance("hygiene-areas", "Area Compliance", "Compliance by monitored area", FaLayerGroup, "green", [
      { label: "Kitchen", value: 97 }, { label: "Dining", value: 95 }, { label: "Store", value: 93 }, { label: "Cash Counter", value: 91 },
    ]),
    table("hygiene-events", "Compliance Events", "Recent compliance checks", FaTable, "blue", ["Area", "Checks", "Score", "Status"], [
      ["Kitchen", "142", "97%", "Good"], ["Dining", "118", "95%", "Good"], ["Store", "96", "93%", "Good"], ["Counter", "82", "91%", "Watch"],
    ]),
  ],

  "captain-behaviour": [
    kpi("captain-events", "Captain Events", "Detected captain activities", FaUserCheck, "blue", "428", "+7.3%", true, "Today"),
    kpi("captain-mobile", "Mobile Usage", "Captain mobile usage events", FaDatabase, "orange", "12", "-1.2%", false, "Within monitored hours"),
    line("captain-trend", "Captain Activity Trend", "Hourly captain activity", FaChartLine, "blue", ["10", "11", "12", "1", "2", "3", "4"], [21, 34, 28, 49, 54, 46, 58]),
    progress("captain-compliance", "Behaviour Compliance", "Captain behaviour score", FaUserCheck, "green", "92%", 92, "Within expected behaviour"),
    activity("captain-activity", "Recent Captain Events", "Latest detected activity", FaList, "purple", [
      ["Captain entered dining area", "3 min ago", "Active"], ["Mobile usage detected", "12 min ago", "Review"],
      ["Captain returned to counter", "21 min ago", "Normal"],
    ]),
  ],

  "wastage-monitoring": [
    kpi("wastage-events", "Wastage Events", "Bin throw events today", FaTrash, "orange", "27", "+5.4%", false, "4 high-volume events"),
    kpi("wastage-volume", "Estimated Wastage", "Estimated wastage volume", FaDatabase, "red", "38.6 kg", "-3.1%", true, "Compared with yesterday"),
    bar("wastage-trend", "Wastage Trend", "Wastage events by day", FaChartBar, "orange", ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"], [22, 26, 18, 31, 24, 27]),
    donut("wastage-category", "Wastage Distribution", "Distribution by category", FaChartPie, "orange", ["Food", "Packaging", "Kitchen", "Other"], [42, 28, 18, 12]),
    table("wastage-events-list", "Recent Wastage Events", "Latest bin events", FaTable, "red", ["Time", "Area", "Events", "Status"], [
      ["3:14 PM", "Kitchen", "8", "Review"], ["2:48 PM", "Dining", "5", "Normal"],
      ["1:42 PM", "Kitchen", "7", "Review"], ["12:36 PM", "Store", "3", "Normal"],
    ]),
  ],

  "cash-counter": [
    kpi("cash-visibility", "Counter Visibility", "Cash counter visibility", FaDollarSign, "green", "96%", "+2.7%", true, "Target 95%"),
    kpi("cash-events", "Payment Events", "Detected payment events", FaDatabase, "blue", "3,284", "+8.2%", true, "Today"),
    line("cash-trend", "Payment Activity", "Hourly payment activity", FaChartLine, "green", ["10", "11", "12", "1", "2", "3", "4"], [120, 164, 212, 286, 251, 304, 328]),
    donut("cash-status", "Counter Status", "Visibility distribution", FaChartPie, "green", ["Visible", "Not Visible"], [96, 4]),
    activity("cash-events-list", "Recent Counter Activity", "Latest payment activity", FaList, "blue", [
      ["Payment activity detected", "2 min ago", "Visible"], ["Counter visibility restored", "9 min ago", "Resolved"],
      ["Payment area checked", "17 min ago", "Verified"],
    ]),
  ],

  "material-inward": [
    kpi("material-arrivals", "Material Arrivals", "Arrivals detected today", FaTruck, "blue", "32", "+6.8%", true, "28 processed"),
    kpi("material-pending", "Pending Arrivals", "Arrivals awaiting processing", FaClock, "orange", "4", "-2", true, "Average wait 14 min"),
    bar("material-trend", "Material Activity", "Daily arrival trend", FaChartBar, "blue", ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"], [24, 29, 26, 34, 30, 32]),
    progress("material-processing", "Processing Efficiency", "Material processing rate", FaCheckCircle, "green", "88%", 88, "28 of 32 processed"),
    table("material-table", "Material Inward Log", "Recent arrivals", FaTable, "blue", ["Time", "Material", "Quantity", "Status"], [
      ["3:24 PM", "Chicken", "120 kg", "Processed"], ["2:52 PM", "Vegetables", "68 kg", "Processed"],
      ["2:18 PM", "Packaging", "42 box", "Pending"], ["1:46 PM", "Oil", "35 L", "Processed"],
    ]),
  ],

  "store-entry-exit": [
    kpi("store-entries", "Store Entries", "Entries detected today", FaSignInAlt, "blue", "428", "+7.1%", true, "Across monitored areas"),
    kpi("store-exits", "Store Exits", "Exits detected today", FaSignOutAlt, "purple", "396", "+5.4%", true, "Net movement +32"),
    line("store-trend", "Entry / Exit Trend", "Hourly movement", FaChartLine, "blue", ["10", "11", "12", "1", "2", "3", "4"], [44, 62, 71, 84, 65, 77, 88]),
    table("store-performance", "Store Activity", "Area-wise movement", FaStore, "purple", ["Area", "Entries", "Exits", "Status"], [
      ["Main Store", "182", "166", "Normal"], ["Kitchen Store", "126", "118", "Normal"],
      ["Receiving", "78", "71", "Normal"], ["Back Area", "42", "41", "Normal"],
    ]),
    compliance("store-status", "Movement Status", "Current movement condition", FaSignInAlt, "green", [
      { label: "Main Store", value: 94 }, { label: "Kitchen", value: 91 }, { label: "Receiving", value: 88 }, { label: "Back Area", value: 96 },
    ]),
  ],

  "dining-area-footfall": [
    kpi("dining-footfall", "Total Footfall", "Customers detected today", FaUsers, "purple", "8,426", "+11.6%", true, "Peak at 8 PM"),
    progress("dining-current", "Current Occupancy", "Current dining occupancy", FaUtensils, "orange", "72%", 72, "144 of 200 seats occupied"),
    kpi("dining-peak", "Peak Hour", "Highest footfall period", FaClock, "red", "8 PM", "+14%", true, "1,210 customers"),
    bar("dining-hour", "Footfall by Hour", "Customer count by hour", FaChartBar, "purple", ["12", "1", "2", "3", "4", "5", "6", "7", "8"], [280, 340, 410, 380, 520, 690, 840, 980, 1210]),
    area("dining-trend", "Occupancy Trend", "Dining occupancy throughout the day", FaChartLine, "orange", ["12", "2", "4", "6", "8", "10"], [31, 42, 48, 61, 84, 72]),
    table("dining-zones", "Dining Zone Performance", "Zone-wise occupancy", FaTable, "purple", ["Zone", "Occupancy", "Seats", "Status"], [
      ["Main Hall", "82%", "96", "Busy"], ["Family Area", "68%", "42", "Normal"],
      ["Window Area", "74%", "38", "Normal"], ["Outdoor", "54%", "24", "Normal"],
    ]),
  ],

  "kabab-monitoring": [
    kpi("kabab-events", "Kabab Events", "Kabab preparation events", FaFire, "orange", "248", "+8.6%", true, "Today"),
    kpi("kabab-duration", "Average Duration", "Average frying duration", FaClock, "red", "18 min", "-2.4%", true, "Target 20 min"),
    progress("kabab-compliance", "Process Compliance", "Kabab process compliance", FaCheckCircle, "green", "94%", 94, "Within expected duration"),
    line("kabab-trend", "Preparation Trend", "Preparation events by hour", FaChartLine, "orange", ["10", "11", "12", "1", "2", "3", "4"], [18, 24, 36, 42, 51, 44, 33]),
    donut("kabab-status", "Preparation Status", "Current preparation distribution", FaChartPie, "orange", ["Within Time", "Delayed", "Review"], [72, 18, 10]),
    table("kabab-table", "Recent Kabab Sessions", "Latest preparation sessions", FaTable, "red", ["Time", "Duration", "Batch", "Status"], [
      ["3:26 PM", "17 min", "K-142", "Good"], ["3:08 PM", "19 min", "K-141", "Good"],
      ["2:48 PM", "23 min", "K-140", "Review"], ["2:26 PM", "18 min", "K-139", "Good"],
    ]),
  ],
};

const ALL_COMPONENTS = Object.values(COMPONENTS).flat();
const COMPONENT_MAP = Object.fromEntries(ALL_COMPONENTS.map((c) => [c.id, c]));
const getComponents = (moduleId) => COMPONENTS[moduleId] || [];

/* -------------------------------------------------------------------------- */
/*  Layout helpers                                                            */
/* -------------------------------------------------------------------------- */

let rowCounter = 0;
function createRow(columns = 3) {
  rowCounter += 1;
  return {
    id: `row-${Date.now()}-${rowCounter}-${Math.random().toString(36).slice(2, 6)}`,
    columns,
    widgets: [],
  };
}

const createDefaultLayout = () => ({ rows: [createRow(3), createRow(3), createRow(3)] });

function getDefaultLayouts() {
  const layouts = {};
  MODULES.forEach((m) => { layouts[m.id] = createDefaultLayout(); });
  return layouts;
}

function safeParse(value, fallback) {
  try { return JSON.parse(value); } catch { return fallback; }
}

/** Drops unknown/duplicate card ids and invalid rows from saved data (removes old ROI data too). */
function sanitizeLayouts(raw) {
  const defaults = getDefaultLayouts();
  const result = {};

  MODULES.forEach((module) => {
    const rows = raw?.[module.id]?.rows;
    if (!Array.isArray(rows) || rows.length === 0) {
      result[module.id] = defaults[module.id];
      return;
    }

    const validIds = new Set(getComponents(module.id).map((c) => c.id));
    const seen = new Set();

    result[module.id] = {
      rows: rows.map((row) => {
        const columns = Math.min(6, Math.max(1, Number(row?.columns) || 3));
        const widgets = (row?.widgets || [])
          .filter((id) => {
            if (!id || !validIds.has(id) || seen.has(id)) return false;
            seen.add(id);
            return true;
          })
          .slice(0, columns);
        return { id: row?.id || createRow(columns).id, columns, widgets };
      }),
    };
  });

  return result;
}

// Module-level drag state (more reliable than reading dataTransfer on drop).
let dragPayload = null;

/* -------------------------------------------------------------------------- */
/*  Theme                                                                     */
/* -------------------------------------------------------------------------- */

const COLORS = {
  blue: { bg: "bg-blue-50", icon: "bg-blue-100 text-blue-600", text: "text-blue-600", bar: "bg-blue-500", stroke: "#2563eb" },
  green: { bg: "bg-emerald-50", icon: "bg-emerald-100 text-emerald-600", text: "text-emerald-600", bar: "bg-emerald-500", stroke: "#059669" },
  purple: { bg: "bg-violet-50", icon: "bg-violet-100 text-violet-600", text: "text-violet-600", bar: "bg-violet-500", stroke: "#7c3aed" },
  orange: { bg: "bg-orange-50", icon: "bg-orange-100 text-orange-600", text: "text-orange-600", bar: "bg-orange-500", stroke: "#ea580c" },
  red: { bg: "bg-red-50", icon: "bg-red-100 text-red-600", text: "text-red-600", bar: "bg-red-500", stroke: "#dc2626" },
  teal: { bg: "bg-cyan-50", icon: "bg-cyan-100 text-cyan-600", text: "text-cyan-600", bar: "bg-cyan-500", stroke: "#0891b2" },
};
const colorClasses = (color) => COLORS[color] || COLORS.blue;

const DONUT_COLORS = ["#2563eb", "#059669", "#7c3aed", "#ea580c"];

const focusRing =
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1";

/* -------------------------------------------------------------------------- */
/*  Header                                                                    */
/* -------------------------------------------------------------------------- */

function EmpireHeader() {
  const navigate = useNavigate();

  const handleLogout = () => {
    try {
      localStorage.clear();
      sessionStorage.removeItem(SESSION_MODULE_KEY);
    } catch {
      /* storage unavailable */
    }
    navigate(LOGIN_PATH, { replace: true });
  };

  return (
    <>
      <header className="h-[68px] shrink-0 border-b border-slate-200 bg-white flex items-center justify-between px-6 z-50">
        <div className="flex items-center h-full">
          <img src={CITRIOT_LOGO} alt="Citriot" className="h-9 w-auto object-contain pr-6" />
          <div className="h-8 w-px bg-slate-200" />
          <img src={EMPIRE_LOGO} alt="Empire" className="h-9 w-auto object-contain pl-6" />
        </div>

        <div className="flex items-center gap-4">
          <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 text-xs font-semibold text-emerald-700">
            <span className="relative flex w-2 h-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping" />
              <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-500" />
            </span>
            System online
          </div>

          <div className="h-8 w-px bg-slate-200" />

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold">
              A
            </div>
            <div className="hidden sm:block">
              <p className="text-sm font-semibold text-slate-800 leading-tight">Admin</p>
              <p className="text-xs text-slate-400 mt-0.5">Administrator</p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleLogout}
            className={`h-10 px-4 rounded-xl border border-slate-200 bg-white text-slate-600 text-xs font-semibold flex items-center gap-2 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition ${focusRing}`}
          >
            <FaSignOutAlt size={13} />
            <span className="hidden sm:inline">Log out</span>
          </button>
        </div>
      </header>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*  Module selection                                                          */
/* -------------------------------------------------------------------------- */

function ModuleSelection({ selectedModules, setSelectedModules, onStart }) {
  const toggleModule = (id) =>
    setSelectedModules((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const allSelected = selectedModules.length === MODULES.length;
  const pct = (selectedModules.length / MODULES.length) * 100;

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-slate-50">
      <div className="flex-1 min-h-0 overflow-y-auto px-6 sm:px-8 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
                Choose your modules
              </h1>
              <p className="text-slate-500 mt-2 max-w-2xl leading-relaxed">
                Pick the Empire modules to include. Each one gets its own canvas,
                so you can arrange its cards and charts independently.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSelectedModules(MODULES.map((m) => m.id))}
                disabled={allSelected}
                className={`px-4 h-10 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition ${focusRing}`}
              >
                Select all
              </button>
              <button
                type="button"
                onClick={() => setSelectedModules([])}
                disabled={selectedModules.length === 0}
                className={`px-4 h-10 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition ${focusRing}`}
              >
                Clear
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 pb-4">
            {MODULES.map((module) => {
              const selected = selectedModules.includes(module.id);
              const Icon = module.icon;

              return (
                <button
                  key={module.id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => toggleModule(module.id)}
                  className={`relative overflow-hidden text-left rounded-2xl border p-5 transition-all ${focusRing} ${
                    selected
                      ? "bg-blue-50/50 border-blue-500 shadow-[0_6px_24px_rgba(37,99,235,0.10)]"
                      : "bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm"
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div
                      className={`w-11 h-11 rounded-xl flex items-center justify-center transition ${
                        selected ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      <Icon size={18} />
                    </div>

                    <div
                      className={`w-6 h-6 rounded-full border flex items-center justify-center transition ${
                        selected
                          ? "bg-blue-600 border-blue-600 text-white"
                          : "bg-white border-slate-300"
                      }`}
                    >
                      {selected && <FaCheck size={10} />}
                    </div>
                  </div>

                  <h3 className="font-semibold text-slate-800 mt-5">{module.title}</h3>
                  <p className="text-xs text-slate-500 mt-1 leading-5">{module.description}</p>
                  <p className="text-[11px] text-slate-400 mt-3">
                    {getComponents(module.id).length} components
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer stays visible no matter how long the grid is */}
      <div className="shrink-0 border-t border-slate-200 bg-white px-6 sm:px-8 py-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4 w-full sm:w-auto">
            <div className="hidden sm:flex w-10 h-10 rounded-xl bg-blue-50 text-blue-600 items-center justify-center shrink-0">
              <FaInfoCircle />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800">
                {selectedModules.length} of {MODULES.length} modules selected
              </p>
              <div className="mt-2 w-48 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          </div>

          <button
            type="button"
            disabled={selectedModules.length === 0}
            onClick={onStart}
            className={`w-full sm:w-auto min-w-[190px] px-6 h-12 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition ${focusRing} ${
              selectedModules.length
                ? "bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-600/20"
                : "bg-slate-100 text-slate-400 cursor-not-allowed"
            }`}
          >
            Start building
            <FaArrowRight size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Sidebar                                                                   */
/* -------------------------------------------------------------------------- */

function ModuleSidebar({
  selectedModules, activeModule, setActiveModule, collapsed, setCollapsed,
  onChangeModules, placedCounts,
}) {
  const items = selectedModules
    .map((id) => MODULES.find((m) => m.id === id))
    .filter(Boolean);

  if (collapsed) {
    return (
      <aside className="w-[72px] shrink-0 bg-white border-r border-slate-200 flex flex-col h-full">
        <div className="h-16 flex items-center justify-center border-b border-slate-100">
          <button
            type="button"
            aria-label="Expand sidebar"
            onClick={() => setCollapsed(false)}
            className={`w-9 h-9 rounded-lg border border-slate-200 text-slate-500 flex items-center justify-center hover:bg-slate-50 ${focusRing}`}
          >
            <FaChevronRight size={12} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto py-4 px-2">
          {items.map((module) => {
            const Icon = module.icon;
            const active = activeModule === module.id;
            return (
              <button
                key={module.id}
                type="button"
                title={module.title}
                onClick={() => setActiveModule(module.id)}
                className={`w-full h-11 rounded-xl flex items-center justify-center mb-2 ${focusRing} ${
                  active ? "bg-blue-600 text-white shadow-sm" : "text-slate-500 hover:bg-slate-50"
                }`}
              >
                <Icon size={16} />
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={onChangeModules}
          title="Change modules"
          className={`m-2 h-10 rounded-xl border border-slate-200 text-slate-500 flex items-center justify-center hover:bg-slate-50 ${focusRing}`}
        >
          <FaCog size={14} />
        </button>
      </aside>
    );
  }

  return (
    <aside className="w-[250px] shrink-0 bg-white border-r border-slate-200 flex flex-col h-full">
      <div className="h-[68px] px-5 border-b border-slate-100 flex items-center justify-between">
        <div>
          <p className="text-sm font-bold text-slate-800">Modules</p>
          <p className="text-[11px] text-slate-400 mt-0.5">{items.length} selected</p>
        </div>
        <button
          type="button"
          aria-label="Collapse sidebar"
          onClick={() => setCollapsed(true)}
          className={`w-9 h-9 rounded-lg border border-slate-200 text-slate-500 flex items-center justify-center hover:bg-slate-50 ${focusRing}`}
        >
          <FaChevronLeft size={12} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3">
        {items.map((module) => {
          const Icon = module.icon;
          const active = activeModule === module.id;
          const total = getComponents(module.id).length;
          const placed = placedCounts[module.id] || 0;

          return (
            <button
              key={module.id}
              type="button"
              onClick={() => setActiveModule(module.id)}
              className={`w-full text-left rounded-xl px-3 py-2.5 mb-1.5 flex items-center gap-3 transition ${focusRing} ${
                active ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <div
                className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                  active ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500"
                }`}
              >
                <Icon size={15} />
              </div>

              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold truncate">{module.title}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  {placed} of {total} placed
                </p>
              </div>
            </button>
          );
        })}
      </div>

      <div className="p-3 border-t border-slate-100">
        <button
          type="button"
          onClick={onChangeModules}
          className={`w-full h-10 rounded-xl border border-slate-200 text-slate-600 text-xs font-semibold flex items-center justify-center gap-2 hover:bg-slate-50 transition ${focusRing}`}
        >
          <FaCog size={12} />
          Change modules
        </button>
      </div>
    </aside>
  );
}

/* -------------------------------------------------------------------------- */
/*  Component library (right panel)                                           */
/* -------------------------------------------------------------------------- */

function ComponentLibrary({ moduleId, onAdd, onDragStart, placedIds }) {
  const [search, setSearch] = useState("");
  const components = getComponents(moduleId);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return components;
    return components.filter(
      (item) =>
        item.title.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query) ||
        item.type.toLowerCase().includes(query)
    );
  }, [components, search]);

  const placedCount = components.filter((c) => placedIds.has(c.id)).length;

  return (
    <aside className="w-[320px] shrink-0 bg-white border-l border-slate-200 h-full flex flex-col">
      <div className="px-5 py-4 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-bold text-slate-800">Components</p>
            <p className="text-xs text-slate-400 mt-0.5">
              Drag a card onto the canvas, or click +
            </p>
          </div>
          <span className="px-2 h-6 rounded-md bg-blue-50 text-blue-600 text-xs font-bold flex items-center">
            {placedCount}/{components.length}
          </span>
        </div>

        <div className="mt-4 relative">
          <FaSearch size={12} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search components"
            aria-label="Search components"
            className="w-full h-10 pl-9 pr-3 rounded-xl border border-slate-200 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
        {filtered.map((component) => {
          const Icon = component.icon;
          const colors = colorClasses(component.color);
          const placed = placedIds.has(component.id);

          return (
            <div
              key={component.id}
              draggable={!placed}
              onDragStart={placed ? undefined : (e) => onDragStart(e, component.id)}
              className={`border rounded-xl p-3.5 transition ${
                placed
                  ? "bg-slate-50 border-slate-100 opacity-60 cursor-not-allowed"
                  : "bg-white border-slate-200 hover:border-blue-300 hover:shadow-sm cursor-grab active:cursor-grabbing"
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl ${colors.icon} flex items-center justify-center shrink-0`}>
                  <Icon size={16} />
                </div>

                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold text-slate-700 truncate">{component.title}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5 truncate">{component.description}</p>
                </div>

                <button
                  type="button"
                  disabled={placed}
                  onClick={() => onAdd(component.id)}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition ${focusRing} ${
                    placed
                      ? "bg-emerald-50 text-emerald-600 cursor-not-allowed"
                      : "bg-slate-100 text-slate-500 hover:bg-blue-600 hover:text-white"
                  }`}
                  title={placed ? "Already added" : "Add to dashboard"}
                  aria-label={placed ? `${component.title} already added` : `Add ${component.title}`}
                >
                  {placed ? <FaCheck size={11} /> : <FaPlus size={11} />}
                </button>
              </div>

              <div className="mt-2.5 flex items-center gap-2">
                <span className="text-[10px] font-medium capitalize text-slate-400">
                  {component.type}
                </span>
                {placed ? (
                  <span className="ml-auto text-[10px] font-semibold text-emerald-600">Added</span>
                ) : (
                  <FaGripVertical size={10} className="ml-auto text-slate-300" />
                )}
              </div>
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="py-12 text-center">
            <FaSearch size={22} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm font-semibold text-slate-500">No components found</p>
            <p className="text-xs text-slate-400 mt-1">Try a different search term.</p>
          </div>
        )}
      </div>
    </aside>
  );
}

/* -------------------------------------------------------------------------- */
/*  Charts                                                                    */
/* -------------------------------------------------------------------------- */

function MiniLineChart({ values, color = "blue", filled = false }) {
  const width = 360;
  const height = 125;
  const pad = 12;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const points = values.map((value, i) => {
    const x = pad + (i * (width - pad * 2)) / Math.max(values.length - 1, 1);
    const y = height - pad - ((value - min) / range) * (height - pad * 2);
    return [x, y];
  });

  const polyline = points.map((p) => p.join(",")).join(" ");
  const polygon = `${pad},${height - pad} ${polyline} ${width - pad},${height - pad}`;
  const stroke = colorClasses(color).stroke;

  return (
    <div className="w-full h-[150px]">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full" preserveAspectRatio="none">
        {[25, 62, 100].map((y) => (
          <line key={y} x1="12" y1={y} x2="348" y2={y} stroke="#e2e8f0" strokeWidth="1" />
        ))}
        {filled && <polygon points={polygon} fill={stroke} opacity="0.10" />}
        <polyline
          points={polyline}
          fill="none"
          stroke={stroke}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {points.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="3.5" fill="white" stroke={stroke} strokeWidth="2" />
        ))}
      </svg>
    </div>
  );
}

function MiniBarChart({ values, color = "blue" }) {
  const max = Math.max(...values, 1);
  const bg = colorClasses(color).bar;

  return (
    <div className="h-[150px] flex items-end gap-2 px-2">
      {values.map((value, i) => (
        <div key={i} className="flex-1 h-full flex flex-col justify-end">
          <div
            className={`w-full max-w-[32px] mx-auto rounded-t-md ${bg} opacity-85`}
            style={{ height: `${Math.max((value / max) * 100, 5)}%` }}
          />
        </div>
      ))}
    </div>
  );
}

function MiniDonut({ values }) {
  const total = values.reduce((s, v) => s + v, 0) || 1;
  let cumulative = 0;

  const segments = values.map((value, i) => {
    const start = (cumulative / total) * 360;
    cumulative += value;
    const end = (cumulative / total) * 360;
    return `${DONUT_COLORS[i % DONUT_COLORS.length]} ${start}deg ${end}deg`;
  });

  return (
    <div className="flex items-center justify-center py-2">
      <div
        className="w-32 h-32 rounded-full flex items-center justify-center"
        style={{ background: `conic-gradient(${segments.join(", ")})` }}
      >
        <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center">
          <div className="text-center">
            <p className="text-xl font-bold text-slate-800">{values[0]}%</p>
            <p className="text-[9px] text-slate-400">Primary</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Widgets                                                                   */
/* -------------------------------------------------------------------------- */

function WidgetHeader({ component, showGrip = true }) {
  const Icon = component.icon;
  const colors = colorClasses(component.color);

  return (
    <div className="flex items-start gap-3 pr-6">
      <div className={`w-9 h-9 rounded-lg ${colors.icon} flex items-center justify-center shrink-0`}>
        <Icon size={15} />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="text-xs font-bold text-slate-800 truncate">{component.title}</h3>
        <p className="text-[10px] text-slate-400 mt-0.5 truncate">{component.description}</p>
      </div>
      {showGrip && <FaGripVertical size={12} className="text-slate-300 mt-1 shrink-0" />}
    </div>
  );
}

function KpiWidget({ component, preview }) {
  const colors = colorClasses(component.color);
  const Icon = component.icon;

  return (
    <div className="h-full">
      <WidgetHeader component={component} showGrip={!preview} />

      <div className="mt-5 flex items-end justify-between gap-3">
        <div>
          <p className="text-2xl font-bold text-slate-900 tracking-tight">{component.value}</p>
          <div className="flex items-center gap-2 mt-2">
            <span className={`text-[10px] font-bold ${component.trendUp ? "text-emerald-600" : "text-red-500"}`}>
              {component.trend.startsWith("-") ? "↓" : "↑"} {component.trend.replace(/^[+-]/, "")}
            </span>
            <span className="text-[10px] text-slate-400">vs previous</span>
          </div>
        </div>

        <div className={`w-11 h-11 rounded-xl ${colors.bg} ${colors.text} flex items-center justify-center`}>
          <Icon size={18} />
        </div>
      </div>

      <div className="mt-5 pt-3 border-t border-slate-100">
        <p className="text-[10px] text-slate-400">{component.secondary}</p>
      </div>
    </div>
  );
}

function ProgressWidget({ component, preview }) {
  const colors = colorClasses(component.color);
  const Icon = component.icon;

  return (
    <div>
      <WidgetHeader component={component} showGrip={!preview} />

      <div className="mt-6 flex items-center justify-between">
        <div>
          <p className="text-3xl font-bold text-slate-900">{component.value}</p>
          <p className="text-[10px] text-slate-400 mt-1">Current performance</p>
        </div>
        <div className={`w-11 h-11 rounded-xl ${colors.icon} flex items-center justify-center`}>
          <Icon size={17} />
        </div>
      </div>

      <div className="mt-5">
        <div className="flex justify-between mb-2">
          <span className="text-[10px] text-slate-400">Progress</span>
          <span className="text-[10px] font-bold text-slate-600">{component.progress}%</span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className={`h-full ${colors.bar} rounded-full`} style={{ width: `${component.progress}%` }} />
        </div>
        <p className="text-[10px] text-slate-400 mt-3">{component.secondary}</p>
      </div>
    </div>
  );
}

function ChartWidget({ component, preview }) {
  const { type, values, color, labels } = component;

  return (
    <div>
      <WidgetHeader component={component} showGrip={!preview} />
      <div className="mt-4">
        {type === "line" && <MiniLineChart values={values} color={color} />}
        {type === "area" && <MiniLineChart values={values} color={color} filled />}
        {type === "bar" && <MiniBarChart values={values} color={color} />}
      </div>
      <div className="flex items-center justify-between mt-1">
        {labels.map((label, i) => (
          <span key={i} className="text-[9px] text-slate-400">{label}</span>
        ))}
      </div>
    </div>
  );
}

function DonutWidget({ component, preview }) {
  return (
    <div>
      <WidgetHeader component={component} showGrip={!preview} />
      <div className="mt-3">
        <MiniDonut values={component.values} />
        <div className="grid grid-cols-2 gap-x-3 gap-y-2 mt-3">
          {component.labels.map((label, i) => (
            <div key={label} className="flex items-center gap-2">
              <span
                className="w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }}
              />
              <span className="text-[10px] text-slate-500 truncate">{label}</span>
              <span className="ml-auto text-[10px] font-bold text-slate-700">{component.values[i]}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ComplianceWidget({ component, preview }) {
  return (
    <div>
      <WidgetHeader component={component} showGrip={!preview} />
      <div className="mt-5 space-y-4">
        {component.items.map((item) => {
          const barColor =
            item.value >= 95 ? "bg-emerald-500" : item.value >= 90 ? "bg-blue-500" : "bg-orange-500";
          return (
            <div key={item.label}>
              <div className="flex justify-between items-center mb-1.5">
                <span className="text-[10px] font-medium text-slate-500">{item.label}</span>
                <span className="text-[10px] font-bold text-slate-700">{item.value}%</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className={`h-full ${barColor} rounded-full`} style={{ width: `${item.value}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function statusBadgeClass(value) {
  const v = String(value).toLowerCase();
  if (["good", "healthy", "normal", "processed", "visible", "busy"].some((k) => v.includes(k)))
    return "bg-emerald-50 text-emerald-600";
  if (["watch", "pending", "review"].some((k) => v.includes(k)))
    return "bg-orange-50 text-orange-600";
  return "bg-blue-50 text-blue-600";
}

function TableWidget({ component, preview }) {
  return (
    <div>
      <WidgetHeader component={component} showGrip={!preview} />
      <div className="mt-4 border border-slate-100 rounded-xl overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-slate-50">
            <tr>
              {component.columns.map((column) => (
                <th key={column} className="px-2.5 py-2 text-[9px] font-bold text-slate-400">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {component.rows.map((row, rowIndex) => (
              <tr key={rowIndex} className="border-t border-slate-100">
                {row.map((value, i) => (
                  <td
                    key={i}
                    className={`px-2.5 py-2.5 text-[10px] ${
                      i === 0 ? "font-semibold text-slate-700" : "text-slate-500"
                    }`}
                  >
                    {i === row.length - 1 ? (
                      <span className={`inline-flex px-1.5 py-0.5 rounded-md text-[9px] font-semibold ${statusBadgeClass(value)}`}>
                        {value}
                      </span>
                    ) : (
                      value
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ActivityWidget({ component, preview }) {
  return (
    <div>
      <WidgetHeader component={component} showGrip={!preview} />
      <div className="mt-5 space-y-3">
        {component.items.map((item, i) => (
          <div key={i} className="flex items-start gap-3">
            <div className="mt-1.5 w-2 h-2 rounded-full bg-blue-500 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-semibold text-slate-700">{item.title}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[10px] text-slate-400">{item.time}</span>
                <span className={`text-[9px] px-1.5 py-0.5 rounded-md font-semibold ${statusBadgeClass(item.status)}`}>
                  {item.status}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SummaryWidget({ component, preview }) {
  return (
    <div>
      <WidgetHeader component={component} showGrip={!preview} />
      <div className="grid grid-cols-2 gap-3 mt-5">
        {component.items.map(([label, value]) => (
          <div key={label} className="rounded-xl bg-slate-50 p-3">
            <p className="text-[10px] text-slate-400">{label}</p>
            <p className="text-base font-bold text-slate-800 mt-1">{value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function WidgetContent({ component, preview }) {
  const props = { component, preview };
  switch (component.type) {
    case "kpi": return <KpiWidget {...props} />;
    case "progress": return <ProgressWidget {...props} />;
    case "line":
    case "area":
    case "bar": return <ChartWidget {...props} />;
    case "donut": return <DonutWidget {...props} />;
    case "compliance": return <ComplianceWidget {...props} />;
    case "table": return <TableWidget {...props} />;
    case "activity": return <ActivityWidget {...props} />;
    case "summary": return <SummaryWidget {...props} />;
    default: return null;
  }
}

function DashboardWidget({ component, onRemove, onDragStart, onDrop, onDragOver, preview = false }) {
  return (
    <div
      draggable={!preview}
      onDragStart={preview ? undefined : (e) => onDragStart(e, component.id)}
      onDragOver={preview ? undefined : (e) => { e.preventDefault(); onDragOver?.(); }}
      onDrop={preview ? undefined : (e) => { e.preventDefault(); onDrop?.(component.id); }}
      className={`relative h-full min-h-[230px] bg-white border border-slate-200 rounded-2xl p-4 ${
        preview ? "" : "cursor-grab active:cursor-grabbing hover:border-blue-300 hover:shadow-sm transition"
      }`}
    >
      {!preview && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(component.id); }}
          className={`absolute top-3 right-3 z-10 w-6 h-6 rounded-md text-slate-300 hover:bg-red-50 hover:text-red-500 flex items-center justify-center ${focusRing}`}
          title="Remove from dashboard"
          aria-label={`Remove ${component.title}`}
        >
          <FaTimes size={10} />
        </button>
      )}
      <WidgetContent component={component} preview={preview} />
    </div>
  );
}

function EmptyCell({ onDrop, onDragOver, onDragEnter, onDragLeave, active }) {
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); onDragOver?.(); }}
      onDragEnter={(e) => { e.preventDefault(); onDragEnter?.(); }}
      onDragLeave={onDragLeave}
      onDrop={(e) => { e.preventDefault(); onDrop?.(); }}
      className={`h-full min-h-[230px] rounded-2xl border-2 border-dashed flex flex-col items-center justify-center transition ${
        active ? "border-blue-400 bg-blue-50/60" : "border-slate-200 bg-slate-50/40"
      }`}
    >
      <div
        className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 ${
          active ? "bg-blue-100 text-blue-600" : "bg-white border border-slate-200 text-slate-400"
        }`}
      >
        <FaPlus size={14} />
      </div>
      <p className="text-xs font-semibold text-slate-500">Drop a component here</p>
      <p className="text-[10px] text-slate-400 mt-1">Drag one from the right panel</p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Canvas                                                                    */
/* -------------------------------------------------------------------------- */

function RowHeader({ row, index, onColumnsChange, onMoveUp, onMoveDown, onDelete, canMoveDown, canDelete }) {
  const iconBtn =
    `w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed ${focusRing}`;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-bold">
          {index + 1}
        </div>
        <div>
          <p className="text-xs font-bold text-slate-700">Row {index + 1}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">
            {row.widgets.length} of {row.columns} filled
          </p>
        </div>
      </div>

      <div className="flex items-center gap-1.5">
        <span className="hidden md:block text-[10px] text-slate-400 font-semibold mr-1">Columns</span>

        {[1, 2, 3, 4, 5, 6].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onColumnsChange(n)}
            aria-pressed={row.columns === n}
            className={`w-8 h-8 rounded-lg text-[11px] font-bold transition ${focusRing} ${
              row.columns === n
                ? "bg-blue-600 text-white"
                : "bg-white border border-slate-200 text-slate-500 hover:bg-slate-50"
            }`}
          >
            {n}
          </button>
        ))}

        <div className="w-px h-5 bg-slate-200 mx-1" />

        <button type="button" onClick={onMoveUp} disabled={index === 0} title="Move row up" aria-label="Move row up" className={`${iconBtn} text-slate-500 hover:bg-slate-50`}>
          <FaArrowUp size={10} />
        </button>
        <button type="button" onClick={onMoveDown} disabled={!canMoveDown} title="Move row down" aria-label="Move row down" className={`${iconBtn} text-slate-500 hover:bg-slate-50`}>
          <FaArrowDown size={10} />
        </button>
        <button type="button" onClick={onDelete} disabled={!canDelete} title="Delete row" aria-label="Delete row" className={`${iconBtn} text-slate-400 hover:bg-red-50 hover:text-red-500 hover:border-red-200`}>
          <FaTrash size={10} />
        </button>
      </div>
    </div>
  );
}

function BuilderCanvas({ rows, onRowsChange, onAddComponent, onMoveWidget, onRemoveWidget }) {
  const [dragOverCell, setDragOverCell] = useState(null);

  useEffect(() => {
    const clear = () => setDragOverCell(null);
    window.addEventListener("dragend", clear);
    return () => window.removeEventListener("dragend", clear);
  }, []);

  const dropOnCell = (rowIndex, cellIndex) => {
    setDragOverCell(null);
    const payload = dragPayload;
    dragPayload = null;
    if (!payload) return;

    if (payload.type === "library") onAddComponent(payload.componentId, rowIndex, cellIndex);
    if (payload.type === "widget") onMoveWidget(payload.componentId, rowIndex, cellIndex);
  };

  const changeColumns = (rowIndex, columns) => {
    onRowsChange((previous) => {
      const next = previous.map((r) => ({ ...r, widgets: [...r.widgets] }));
      const target = next[rowIndex];
      if (!target) return previous;

      target.columns = columns;
      if (target.widgets.length <= columns) return next;

      // Move overflow cards into a new row right below, so nothing is lost.
      const overflow = target.widgets.splice(columns);
      const extra = createRow(Math.min(6, Math.max(overflow.length, 1)));
      extra.widgets = overflow;
      next.splice(rowIndex + 1, 0, extra);
      return next;
    });
  };

  const moveRow = (index, direction) => {
    onRowsChange((previous) => {
      const target = index + direction;
      if (target < 0 || target >= previous.length) return previous;
      const next = [...previous];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const deleteRow = (index) => {
    onRowsChange((previous) =>
      previous.length <= 1 ? previous : previous.filter((_, i) => i !== index)
    );
  };

  return (
    <div className="space-y-5 pb-8">
      {rows.map((row, rowIndex) => (
        <section key={row.id} className="bg-white border border-slate-200 rounded-2xl p-4 md:p-5">
          <RowHeader
            row={row}
            index={rowIndex}
            onColumnsChange={(c) => changeColumns(rowIndex, c)}
            onMoveUp={() => moveRow(rowIndex, -1)}
            onMoveDown={() => moveRow(rowIndex, 1)}
            onDelete={() => deleteRow(rowIndex)}
            canMoveDown={rowIndex < rows.length - 1}
            canDelete={rows.length > 1}
          />

          <div
            className="grid gap-4"
            style={{ gridTemplateColumns: `repeat(${row.columns}, minmax(0, 1fr))` }}
          >
            {Array.from({ length: row.columns }).map((_, cellIndex) => {
              const componentId = row.widgets[cellIndex];
              const component = componentId ? COMPONENT_MAP[componentId] : null;
              const cellKey = `${rowIndex}-${cellIndex}`;

              if (component) {
                return (
                  <DashboardWidget
                    key={component.id}
                    component={component}
                    onRemove={onRemoveWidget}
                    onDragStart={(e, id) => {
                      e.dataTransfer.effectAllowed = "move";
                      e.dataTransfer.setData("text/plain", id);
                      dragPayload = { type: "widget", componentId: id };
                    }}
                    onDragOver={() => setDragOverCell(null)}
                    onDrop={(targetId) => {
                      const payload = dragPayload;
                      dragPayload = null;
                      setDragOverCell(null);
                      // Dropping a library card onto an occupied cell does nothing.
                      if (payload?.type === "widget" && payload.componentId !== targetId) {
                        onMoveWidget(payload.componentId, rowIndex, cellIndex);
                      }
                    }}
                  />
                );
              }

              return (
                <EmptyCell
                  key={cellKey}
                  active={dragOverCell === cellKey}
                  onDragEnter={() => setDragOverCell(cellKey)}
                  onDragOver={() => setDragOverCell(cellKey)}
                  onDragLeave={() => setDragOverCell((c) => (c === cellKey ? null : c))}
                  onDrop={() => dropOnCell(rowIndex, cellIndex)}
                />
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Preview modal                                                             */
/* -------------------------------------------------------------------------- */

function PreviewModal({ activeModule, selectedModules, layouts, onClose }) {
  const [previewModule, setPreviewModule] = useState(activeModule);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const module = MODULES.find((m) => m.id === previewModule);
  const layout = layouts[previewModule] || createDefaultLayout();
  const hasWidgets = layout.rows.some((r) => r.widgets.length);

  return (
    <div className="fixed inset-0 z-[100] bg-slate-950/50 backdrop-blur-sm flex items-center justify-center p-5">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Dashboard preview"
        className="w-full max-w-[1500px] h-[92vh] bg-slate-50 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
      >
        <div className="min-h-[68px] bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between gap-4 shrink-0">
          <div className="flex items-center gap-4 min-w-0">
            <div className="shrink-0">
              <p className="text-sm font-bold text-slate-800">Dashboard preview</p>
              <p className="text-xs text-slate-400 mt-0.5">How the selected module will look</p>
            </div>
            <div className="h-8 w-px bg-slate-200 shrink-0" />
            <div className="flex items-center gap-1.5 overflow-x-auto">
              {selectedModules.map((id) => {
                const item = MODULES.find((m) => m.id === id);
                if (!item) return null;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setPreviewModule(id)}
                    className={`px-3 py-2 rounded-lg text-[11px] font-semibold whitespace-nowrap transition ${focusRing} ${
                      previewModule === id
                        ? "bg-blue-600 text-white"
                        : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                    }`}
                  >
                    {item.title}
                  </button>
                );
              })}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close preview"
            className={`w-9 h-9 shrink-0 rounded-lg border border-slate-200 text-slate-500 flex items-center justify-center hover:bg-slate-50 ${focusRing}`}
          >
            <FaTimes size={13} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-[1400px] mx-auto">
            {module && (
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                  <module.icon size={19} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">{module.title}</h2>
                  <p className="text-xs text-slate-400 mt-0.5">{module.description}</p>
                </div>
              </div>
            )}

            <div className="space-y-5">
              {layout.rows.map((row) => {
                const components = row.widgets.map((id) => COMPONENT_MAP[id]).filter(Boolean);
                if (!components.length) return null;
                return (
                  <div
                    key={row.id}
                    className="grid gap-5"
                    style={{ gridTemplateColumns: `repeat(${row.columns}, minmax(0, 1fr))` }}
                  >
                    {components.map((c) => (
                      <DashboardWidget key={c.id} component={c} preview />
                    ))}
                  </div>
                );
              })}

              {!hasWidgets && (
                <div className="min-h-[420px] bg-white border border-slate-200 rounded-2xl flex flex-col items-center justify-center">
                  <div className="w-14 h-14 rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center mb-4">
                    <FaChartBar size={22} />
                  </div>
                  <p className="text-sm font-semibold text-slate-600">Nothing to preview yet</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Add components from the library to see them here.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Builder                                                                   */
/* -------------------------------------------------------------------------- */

function EmpireBuilder({ selectedModules, onChangeModules }) {
  const [activeModule, setActiveModule] = useState(selectedModules[0]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const savedTimer = useRef(null);

  const [layouts, setLayouts] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return getDefaultLayouts();
      const parsed = safeParse(stored, null);
      if (!parsed) return getDefaultLayouts();
      return sanitizeLayouts(parsed.moduleDashboards || parsed.layouts || {});
    } catch {
      return getDefaultLayouts();
    }
  });

  useEffect(() => () => clearTimeout(savedTimer.current), []);

  useEffect(() => {
    if (!activeModule || !selectedModules.includes(activeModule)) {
      setActiveModule(selectedModules[0]);
    }
  }, [selectedModules, activeModule]);

  const currentRows = layouts[activeModule]?.rows || createDefaultLayout().rows;

  const placedIds = useMemo(
    () => new Set(currentRows.flatMap((row) => row.widgets.filter(Boolean))),
    [currentRows]
  );

  const placedCounts = useMemo(() => {
    const counts = {};
    MODULES.forEach((m) => {
      counts[m.id] = (layouts[m.id]?.rows || []).reduce((n, r) => n + r.widgets.length, 0);
    });
    return counts;
  }, [layouts]);

  const setRows = useCallback(
    (updater) => {
      setLayouts((previous) => {
        const current = previous[activeModule]?.rows || createDefaultLayout().rows;
        const nextRows = typeof updater === "function" ? updater(current) : updater;
        if (nextRows === current) return previous;
        return { ...previous, [activeModule]: { rows: nextRows } };
      });
    },
    [activeModule]
  );

  const addComponent = (componentId, targetRowIndex = null, targetCellIndex = null) => {
    // Only cards that belong to the active module can be added.
    if (!getComponents(activeModule).some((c) => c.id === componentId)) return;

    setRows((previousRows) => {
      // Hard rule: each card can be placed only once.
      if (previousRows.some((row) => row.widgets.includes(componentId))) return previousRows;

      const rows = previousRows.map((row) => ({ ...row, widgets: [...row.widgets] }));

      if (targetRowIndex !== null && targetCellIndex !== null) {
        const targetRow = rows[targetRowIndex];
        if (!targetRow || targetRow.widgets[targetCellIndex] || targetRow.widgets.length >= targetRow.columns) {
          return previousRows;
        }
        const at = Math.min(targetCellIndex, targetRow.widgets.length);
        targetRow.widgets.splice(at, 0, componentId);
        return rows;
      }

      for (const row of rows) {
        if (row.widgets.length < row.columns) {
          row.widgets.push(componentId);
          return rows;
        }
      }

      const newRow = createRow(3);
      newRow.widgets.push(componentId);
      rows.push(newRow);
      return rows;
    });
  };

  const removeWidget = (componentId) => {
    setRows((previousRows) =>
      previousRows.map((row) => ({
        ...row,
        widgets: row.widgets.filter((id) => id !== componentId),
      }))
    );
  };

  const moveWidget = (componentId, targetRowIndex, targetCellIndex) => {
    setRows((previousRows) => {
      const rows = previousRows.map((row) => ({ ...row, widgets: [...row.widgets] }));

      let srcRow = -1;
      let srcCell = -1;
      rows.forEach((row, r) => {
        const c = row.widgets.indexOf(componentId);
        if (c !== -1) { srcRow = r; srcCell = c; }
      });

      const targetRow = rows[targetRowIndex];
      if (srcRow === -1 || !targetRow || targetCellIndex >= targetRow.columns) return previousRows;
      if (srcRow === targetRowIndex && srcCell === targetCellIndex) return previousRows;

      const occupant = targetRow.widgets[targetCellIndex];

      if (occupant) {
        // Swap the two cards.
        rows[srcRow].widgets[srcCell] = occupant;
        rows[targetRowIndex].widgets[targetCellIndex] = componentId;
        return rows;
      }

      // Move into an empty cell (rows stay compact, so insert at the nearest free slot).
      if (srcRow !== targetRowIndex && targetRow.widgets.length >= targetRow.columns) return previousRows;

      rows[srcRow].widgets.splice(srcCell, 1);
      const at = Math.min(targetCellIndex, rows[targetRowIndex].widgets.length);
      rows[targetRowIndex].widgets.splice(at, 0, componentId);
      return rows;
    });
  };

  const handleLibraryDragStart = (event, componentId) => {
    event.dataTransfer.effectAllowed = "copy";
    event.dataTransfer.setData("text/plain", componentId);
    dragPayload = { type: "library", componentId };
  };

  const handleSave = () => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          selectedModules,
          moduleDashboards: layouts,
          savedAt: new Date().toISOString(),
        })
      );
      sessionStorage.setItem(SESSION_MODULE_KEY, JSON.stringify(selectedModules));
    } catch {
      /* storage unavailable or full */
    }
    setSaved(true);
    clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSaved(false), 2200);
  };

  const activeModuleData = MODULES.find((m) => m.id === activeModule);

  const handleReset = () => {
    if (!window.confirm(`Reset the ${activeModuleData?.title} layout? This clears every card on this canvas.`)) return;
    setLayouts((previous) => ({ ...previous, [activeModule]: createDefaultLayout() }));
  };

  const totalForModule = getComponents(activeModule).length;

  return (
    <div className="h-screen bg-slate-50 flex flex-col overflow-hidden">
      <EmpireHeader />

      <div className="flex-1 min-h-0 flex overflow-hidden">
        <ModuleSidebar
          selectedModules={selectedModules}
          activeModule={activeModule}
          setActiveModule={setActiveModule}
          collapsed={sidebarCollapsed}
          setCollapsed={setSidebarCollapsed}
          onChangeModules={onChangeModules}
          placedCounts={placedCounts}
        />

        <main className="flex-1 min-w-0 min-h-0 flex flex-col bg-slate-50">
          <div className="shrink-0 px-6 py-4 border-b border-slate-200 bg-white">
            <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
              <div className="flex items-center gap-3">
                {activeModuleData && (
                  <div className="w-11 h-11 rounded-xl bg-blue-600 text-white flex items-center justify-center">
                    <activeModuleData.icon size={17} />
                  </div>
                )}
                <div>
                  <h1 className="text-xl font-bold text-slate-900 leading-tight">
                    {activeModuleData?.title}
                  </h1>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {placedIds.size} of {totalForModule} components placed
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPreviewOpen(true)}
                  className={`h-10 px-4 rounded-xl border border-slate-200 bg-white text-slate-600 text-xs font-semibold flex items-center gap-2 hover:bg-slate-50 transition ${focusRing}`}
                >
                  <FaEye size={13} /> Preview
                </button>

                <button
                  type="button"
                  onClick={handleReset}
                  className={`h-10 px-4 rounded-xl border border-slate-200 bg-white text-slate-600 text-xs font-semibold flex items-center gap-2 hover:bg-slate-50 transition ${focusRing}`}
                >
                  <FaRedo size={11} /> Reset
                </button>

                <button
                  type="button"
                  onClick={handleAddRowClick(setRows)}
                  className={`h-10 px-4 rounded-xl border border-slate-200 bg-white text-slate-600 text-xs font-semibold flex items-center gap-2 hover:bg-slate-50 transition ${focusRing}`}
                >
                  <FaPlus size={11} /> Add row
                </button>

                <button
                  type="button"
                  onClick={handleSave}
                  className={`h-10 px-4 rounded-xl text-white text-xs font-semibold flex items-center gap-2 transition ${focusRing} ${
                    saved ? "bg-emerald-600" : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  {saved ? <FaCheckCircle size={12} /> : <FaSave size={12} />}
                  {saved ? "Saved" : "Save dashboard"}
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
            <BuilderCanvas
              rows={currentRows}
              onRowsChange={setRows}
              onAddComponent={addComponent}
              onMoveWidget={moveWidget}
              onRemoveWidget={removeWidget}
            />
          </div>
        </main>

        <ComponentLibrary
          moduleId={activeModule}
          placedIds={placedIds}
          onAdd={(id) => addComponent(id)}
          onDragStart={handleLibraryDragStart}
        />
      </div>

      {previewOpen && (
        <PreviewModal
          activeModule={activeModule}
          selectedModules={selectedModules}
          layouts={layouts}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
}

const handleAddRowClick = (setRows) => () => setRows((rows) => [...rows, createRow(3)]);

/* -------------------------------------------------------------------------- */
/*  Root                                                                      */
/* -------------------------------------------------------------------------- */

function readStoredModules() {
  try {
    const stored = sessionStorage.getItem(SESSION_MODULE_KEY);
    if (!stored) return [];
    const parsed = safeParse(stored, []);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id) => MODULES.some((m) => m.id === id));
  } catch {
    return [];
  }
}

export default function EmpireDashboard() {
  const [selectedModules, setSelectedModules] = useState(readStoredModules);
  const [building, setBuilding] = useState(() => readStoredModules().length > 0);

  const handleStartBuilding = () => {
    if (!selectedModules.length) return;
    try {
      sessionStorage.setItem(SESSION_MODULE_KEY, JSON.stringify(selectedModules));
    } catch {
      /* storage unavailable */
    }
    setBuilding(true);
  };

  if (!building) {
    return (
      <div className="h-screen flex flex-col overflow-hidden">
        <EmpireHeader />
        <ModuleSelection
          selectedModules={selectedModules}
          setSelectedModules={setSelectedModules}
          onStart={handleStartBuilding}
        />
      </div>
    );
  }

  return (
    <EmpireBuilder
      selectedModules={selectedModules}
      onChangeModules={() => setBuilding(false)}
    />
  );
}